import { useEffect, useRef, useState } from "react";
import { API } from "@/lib/api";
import { toast } from "sonner";

/**
 * Corpus Ingest section — one-click loader for the 10K persona Q&A PDFs.
 *
 * POST /api/admin/corpus/ingest-gold-persona → fire-and-forget background task
 * GET  /api/admin/corpus/ingest-gold-persona/status → live progress
 *
 * Behavior:
 *   - On mount, fetch status once so admin sees prior run state.
 *   - Button "Start ingest" fires POST. Disabled while state=running.
 *   - While running, poll every 3s and render live counts + last PDF.
 *   - On completion (done/error), stop polling and show final counts.
 *
 * Idempotent — safe to re-run (existing case_ids get updated, not duped).
 */
export default function AdminCorpusIngestSection() {
  const [status, setStatus] = useState(null); // {state, ok, skipped, failed, last_pdf, ...}
  const [starting, setStarting] = useState(false);
  const pollRef = useRef(null);

  async function fetchStatus() {
    try {
      const r = await fetch(`${API}/admin/corpus/ingest-gold-persona/status`, {
        credentials: "include",
      });
      if (!r.ok) return null;
      const data = await r.json();
      setStatus(data);
      return data;
    } catch {
      return null;
    }
  }

  useEffect(() => {
    fetchStatus();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  useEffect(() => {
    // Start / stop polling based on live state.
    if (status?.state === "running") {
      if (!pollRef.current) {
        pollRef.current = setInterval(fetchStatus, 3000);
      }
    } else if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, [status?.state]);

  async function handleStart() {
    if (starting) return;
    setStarting(true);
    try {
      const r = await fetch(`${API}/admin/corpus/ingest-gold-persona`, {
        method: "POST",
        credentials: "include",
      });
      const data = await r.json();
      if (!r.ok) {
        toast.error(data?.detail || "Ingest failed to start.");
        return;
      }
      toast.success("Ingest started. This takes ~7 minutes for 10K entries.");
      setStatus({ state: "running", ok: 0, skipped: 0, failed: 0 });
      // Kick off polling immediately.
      fetchStatus();
    } catch (e) {
      toast.error(e?.message || "Network error starting ingest.");
    } finally {
      setStarting(false);
    }
  }

  const s = status || { state: "never_run" };
  const isRunning = s.state === "running";
  const isDone = s.state === "done";
  const isError = s.state === "error";
  const okCount = s.ok || 0;
  const skippedCount = s.skipped || 0;
  const failedCount = s.failed || 0;
  const pct = isRunning ? Math.min(99, Math.round((okCount / 10500) * 100)) : (isDone ? 100 : 0);

  return (
    <div className="border border-[var(--border)] p-5 space-y-4" data-testid="corpus-ingest-panel">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <p className="font-ui text-sm text-[var(--text)] leading-relaxed">
            Loads the <strong>10,509 gold persona Q&amp;A</strong> (score ≥ 9/10) from bundled
            PDFs into <code>hbm_corpus</code> with <code>importance=0.95</code>. Every future
            chat retrieves from this expanded corpus. Idempotent — safe to re-run.
          </p>
          <p className="font-mono-ui text-[10px] tracking-[0.18em] uppercase text-[var(--text-dim)] mt-2">
            ~7 minutes · runs in background · non-blocking
          </p>
        </div>
        <button
          onClick={handleStart}
          disabled={starting || isRunning}
          data-testid="corpus-ingest-start"
          className="px-5 py-2.5 bg-[var(--accent)] text-[var(--bg)] hover:opacity-90 font-mono-ui text-[10px] tracking-[0.28em] uppercase transition-opacity disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
        >
          {starting ? "Starting…" : isRunning ? "Running…" : isDone ? "Re-run" : "Start ingest"}
        </button>
      </div>

      {/* Status row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <StatBox label="State" value={s.state || "never_run"} testid="corpus-state" />
        <StatBox label="Ingested" value={okCount.toLocaleString()} testid="corpus-ok" />
        <StatBox label="Skipped (dup)" value={skippedCount.toLocaleString()} testid="corpus-skipped" />
        <StatBox label="Failed" value={failedCount.toLocaleString()} testid="corpus-failed" />
      </div>

      {/* Progress bar during run */}
      {(isRunning || isDone) && (
        <div>
          <div className="h-1.5 bg-[var(--border)] overflow-hidden">
            <div
              className="h-full bg-[var(--accent)] transition-all duration-500"
              style={{ width: `${pct}%` }}
              data-testid="corpus-progress-bar"
            />
          </div>
          <div className="flex items-center justify-between mt-2 font-mono-ui text-[10px] tracking-[0.2em] uppercase text-[var(--text-dim)]">
            <span data-testid="corpus-last-pdf">
              {s.last_pdf ? `Last part: ${s.last_pdf}` : ""}
            </span>
            <span>{pct}%</span>
          </div>
        </div>
      )}

      {/* Error banner */}
      {isError && s.error && (
        <div
          className="border border-red-500/40 bg-red-500/5 px-4 py-3 text-sm text-red-400 font-ui"
          data-testid="corpus-error"
        >
          {s.error}
        </div>
      )}

      {/* Meta */}
      {s.started_at && (
        <div className="font-mono-ui text-[10px] tracking-[0.18em] uppercase text-[var(--text-dim)]">
          Started {new Date(s.started_at).toLocaleString()}
          {s.started_by ? ` by ${s.started_by}` : ""}
          {s.finished_at && ` · finished ${new Date(s.finished_at).toLocaleString()}`}
        </div>
      )}
    </div>
  );
}

function StatBox({ label, value, testid }) {
  return (
    <div className="border border-[var(--border)] px-3 py-2">
      <div className="font-mono-ui text-[9px] tracking-[0.24em] uppercase text-[var(--text-dim)]">
        {label}
      </div>
      <div className="font-mono-ui text-sm text-[var(--text)] mt-1" data-testid={testid}>
        {value}
      </div>
    </div>
  );
}
